// ============================================================================
// RunSheetViewMenu: one button for what the Run Sheet shows (Lucid 009 B).
// ----------------------------------------------------------------------------
// Peter, 2026-09-26: "combine all these filters like Needs finishing, ready to
// send, awaiting answer, decided, history and make it more nicer to use / view
// and more compact on a smaller screen and less clutter on desktop view." He
// picked direction B, "One view menu".
//
// So the six lane chips, the Status dropdown and the Labels dropdown are ONE
// button showing the current lane and its count. It opens a menu of every
// lane with its count (the current one ticked, a lane with nothing in it
// dimmed but still selectable, History last), then the Status and Labels
// choices the two dropdowns used to hold, with the same options, counts and
// mutual exclusions. A Status or Label filter that is set shows beside the
// button as a removable chip (RunSheetFilterChips), so nothing narrows the
// sheet without being on screen.
//
// This is a presentation change only. The lane, the status set and the label
// set are still DashboardPage's state, with the same keys and the same
// semantics; this file only draws them and reports a change.
// ============================================================================

import React from 'react';
import { Icons } from '../ui/Icon';
import { TONES, resolveTone, type ReviewLabel } from '../../services/review-labels';
import RunSheetMenu from './RunSheetMenu';

export type QuoteStatusFilterKey = 'pdf' | 'sent' | 'unsent' | 'nopdf';

export interface QuoteStatusFilterOption {
    id: QuoteStatusFilterKey;
    label: string;
    description: string;
}

export const QUOTE_STATUS_OPTIONS: QuoteStatusFilterOption[] = [
    { id: 'pdf', label: 'PDF made', description: 'Quote PDF has been generated' },
    { id: 'sent', label: 'Sent', description: 'Quote has been sent to client' },
    { id: 'unsent', label: 'Not sent', description: 'Quote has not been sent yet' },
    { id: 'nopdf', label: 'No PDF made', description: 'Quote does not have a PDF yet' },
];

/** The label facet's sentinel for "has no label". DashboardPage's filter
 *  predicate reads the same string (`r.labelId ?? '__none'`). */
export const UNLABELLED_ID = '__none';

/**
 * Tick or untick one status. Opposite states exclude each other, exactly as
 * the Status dropdown did: PDF made and No PDF made cannot both be on, and
 * neither can Sent and Not sent, because together they match nothing.
 */
export function toggleQuoteStatus(
    selected: ReadonlySet<QuoteStatusFilterKey>,
    id: QuoteStatusFilterKey,
    checked: boolean,
): Set<QuoteStatusFilterKey> {
    const next = new Set(selected);
    if (!checked) {
        next.delete(id);
        return next;
    }
    next.add(id);
    if (id === 'pdf') next.delete('nopdf');
    if (id === 'nopdf') next.delete('pdf');
    if (id === 'sent') next.delete('unsent');
    if (id === 'unsent') next.delete('sent');
    return next;
}

export interface RunSheetViewLane {
    key: string;
    label: string;
    /** Shown on the phone trigger, where the full label does not fit. */
    shortLabel: string;
    hint: string;
    count: number;
    /** CSS colour for the lane's identity (its --lane-tone). */
    tone?: string;
    icon?: keyof typeof Icons;
}

export interface RunSheetViewMenuProps {
    /** All, the four live lanes, then History, in display order. */
    lanes: RunSheetViewLane[];
    value: string;
    onPick: (key: string) => void;
    statusSelected: ReadonlySet<QuoteStatusFilterKey>;
    onStatusChange: (next: Set<QuoteStatusFilterKey>) => void;
    statusCounts?: Partial<Record<QuoteStatusFilterKey, number>>;
    labels: ReviewLabel[];
    labelSelected: ReadonlySet<string>;
    onLabelsChange: (next: Set<string>) => void;
    /** Phone: the trigger shows the lane's short label. */
    compact?: boolean;
    triggerRef?: React.MutableRefObject<HTMLButtonElement | null>;
}

/** A group's Clear leaves the menu with nothing focused unless focus is moved
 *  first, because the button unmounts. Move it to the group's first choice. */
function focusGroupStart(from: HTMLElement) {
    const first = from.closest('[role="group"]')?.querySelector<HTMLElement>('[role="menuitemcheckbox"]');
    try { first?.focus(); } catch { /* detached */ }
}

export default function RunSheetViewMenu({
    lanes, value, onPick, statusSelected, onStatusChange, statusCounts,
    labels, labelSelected, onLabelsChange, compact = false, triggerRef,
}: RunSheetViewMenuProps) {
    const current = lanes.find(l => l.key === value) || lanes[0];
    if (!current) return null;
    const CurrentIcon = (current.icon ? Icons[current.icon] : Icons.List) as any;
    const onLane = current.key !== 'all';
    // The button's name says the lane (in full, even on a phone) and its
    // count, so a screen reader hears what the sheet shows. The lane's hint
    // stays as the title, which then serves as the description.
    const triggerName = `View: ${current.label}, ${current.count} ${current.count === 1 ? 'project' : 'projects'}`;

    return (
        <RunSheetMenu
            label="View"
            width={compact ? 340 : 288}
            align="start"
            className="rs-viewmenu"
            triggerRef={triggerRef}
            triggerClassName="rs-view qm-inline-action-btn"
            triggerAriaLabel={triggerName}
            triggerTitle={current.hint}
            triggerProps={{
                'data-lane': current.key,
                'data-on': onLane ? 'true' : 'false',
                style: current.tone ? { ['--lane-tone' as any]: current.tone } : undefined,
            }}
            triggerContent={(
                <>
                    <CurrentIcon size={13} aria-hidden="true" />
                    <span className="rs-sr">View: </span>
                    <span className="rs-view__label">{compact ? current.shortLabel : current.label}</span>
                    <b className="rs-view__count">{current.count}</b>
                    <Icons.ChevronDown className="rs-view__chevron" size={12} aria-hidden="true" />
                </>
            )}
        >
            {(close) => (
                <>
                    <div role="group" aria-label="Lanes">
                        {lanes.map((l) => {
                            const on = l.key === current.key;
                            const Icon = (l.icon ? Icons[l.icon] : Icons.List) as any;
                            return (
                                <button
                                    key={l.key}
                                    type="button"
                                    role="menuitemradio"
                                    aria-checked={on}
                                    tabIndex={-1}
                                    className="rs-menu__item rs-viewmenu__lane qm-inline-action-btn"
                                    data-lane={l.key}
                                    data-empty={l.count === 0 ? 'true' : 'false'}
                                    style={l.tone ? { ['--lane-tone' as any]: l.tone } : undefined}
                                    title={l.hint}
                                    onClick={() => { onPick(l.key); close(true); }}
                                >
                                    <span className="rs-menu__tick" aria-hidden="true">
                                        {on && <Icons.Check size={13} />}
                                    </span>
                                    <Icon className="rs-viewmenu__icon" size={13} aria-hidden="true" />
                                    <span className="rs-menu__label">{l.label}</span>
                                    <span className="rs-menu__count">{l.count}</span>
                                </button>
                            );
                        })}
                    </div>

                    <div role="separator" className="rs-menu__sep" />

                    <div role="group" aria-label="Quote status" className="rs-viewmenu__group">
                        <div role="none" className="rs-menu__head">
                            <span aria-hidden="true">Status</span>
                            {statusSelected.size > 0 && (
                                <button
                                    type="button"
                                    role="menuitem"
                                    tabIndex={-1}
                                    className="rs-menu__clear qm-inline-action-btn"
                                    aria-label="Clear status filters"
                                    onClick={(e) => { focusGroupStart(e.currentTarget); onStatusChange(new Set()); }}
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                        {QUOTE_STATUS_OPTIONS.map((opt) => {
                            const checked = statusSelected.has(opt.id);
                            const count = statusCounts?.[opt.id];
                            return (
                                <button
                                    key={opt.id}
                                    type="button"
                                    role="menuitemcheckbox"
                                    aria-checked={checked}
                                    tabIndex={-1}
                                    className="rs-menu__item rs-viewmenu__status qm-inline-action-btn"
                                    data-status={opt.id}
                                    title={opt.description}
                                    onClick={() => onStatusChange(toggleQuoteStatus(statusSelected, opt.id, !checked))}
                                >
                                    <span className="rs-menu__box" data-on={checked ? 'true' : 'false'} aria-hidden="true">
                                        {checked && <Icons.Check size={11} />}
                                    </span>
                                    <span className="rs-menu__label">{opt.label}</span>
                                    {count !== undefined && <span className="rs-menu__count">{count}</span>}
                                </button>
                            );
                        })}
                    </div>

                    {/* Labels only when the tenant has any, as before: an
                        empty vocabulary is not a filter. */}
                    {labels.length > 0 && (
                        <div role="group" aria-label="Labels" className="rs-viewmenu__group">
                            <div role="none" className="rs-menu__head">
                                <span aria-hidden="true">Labels</span>
                                {labelSelected.size > 0 && (
                                    <button
                                        type="button"
                                        role="menuitem"
                                        tabIndex={-1}
                                        className="rs-menu__clear qm-inline-action-btn"
                                        aria-label="Clear label filters"
                                        onClick={(e) => { focusGroupStart(e.currentTarget); onLabelsChange(new Set()); }}
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                            {[...labels.map(l => ({ id: l.id, name: l.name, tone: TONES[resolveTone(l.tone)].fg as string | null })),
                                { id: UNLABELLED_ID, name: 'Unlabelled', tone: null }].map((opt) => {
                                const checked = labelSelected.has(opt.id);
                                return (
                                    <button
                                        key={opt.id}
                                        type="button"
                                        role="menuitemcheckbox"
                                        aria-checked={checked}
                                        tabIndex={-1}
                                        className="rs-menu__item rs-viewmenu__label qm-inline-action-btn"
                                        data-label-id={opt.id}
                                        onClick={() => {
                                            const next = new Set(labelSelected);
                                            if (checked) next.delete(opt.id); else next.add(opt.id);
                                            onLabelsChange(next);
                                        }}
                                    >
                                        <span className="rs-menu__box" data-on={checked ? 'true' : 'false'} aria-hidden="true">
                                            {checked && <Icons.Check size={11} />}
                                        </span>
                                        <i
                                            className="rs-menu__tone"
                                            data-empty={opt.tone ? undefined : 'true'}
                                            style={opt.tone ? { ['--label-tone' as any]: opt.tone } : undefined}
                                            aria-hidden="true"
                                        />
                                        <span className="rs-menu__label">{opt.name}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </>
            )}
        </RunSheetMenu>
    );
}

export interface RunSheetFilterChipsProps {
    statusSelected: ReadonlySet<QuoteStatusFilterKey>;
    onStatusChange: (next: Set<QuoteStatusFilterKey>) => void;
    labels: ReviewLabel[];
    labelSelected: ReadonlySet<string>;
    onLabelsChange: (next: Set<string>) => void;
    /** Where focus goes when the last chip is removed. */
    focusFallbackRef?: React.MutableRefObject<HTMLElement | null>;
}

interface Chip {
    key: string;
    text: string;
    kind: 'status' | 'label';
    tone: string | null;
    remove: () => void;
}

/**
 * One removable chip per Status or Label filter that is set. Each chip is a
 * single button (the whole chip is the target, so on a phone it is a 44px
 * target, not a 12px x). Removing one moves focus to the next chip, or to the
 * View button when it was the last.
 */
export function RunSheetFilterChips({
    statusSelected, onStatusChange, labels, labelSelected, onLabelsChange, focusFallbackRef,
}: RunSheetFilterChipsProps) {
    const chips: Chip[] = [];
    for (const opt of QUOTE_STATUS_OPTIONS) {
        if (!statusSelected.has(opt.id)) continue;
        chips.push({
            key: `status:${opt.id}`,
            text: opt.label,
            kind: 'status',
            tone: null,
            remove: () => onStatusChange(toggleQuoteStatus(statusSelected, opt.id, false)),
        });
    }
    const removeLabel = (id: string) => {
        const next = new Set(labelSelected);
        next.delete(id);
        onLabelsChange(next);
    };
    for (const l of labels) {
        if (!labelSelected.has(l.id)) continue;
        chips.push({ key: `label:${l.id}`, text: l.name, kind: 'label', tone: TONES[resolveTone(l.tone)].fg, remove: () => removeLabel(l.id) });
    }
    if (labelSelected.has(UNLABELLED_ID)) {
        chips.push({ key: `label:${UNLABELLED_ID}`, text: 'Unlabelled', kind: 'label', tone: null, remove: () => removeLabel(UNLABELLED_ID) });
    }
    // A selected id whose definition has gone (deleted in another tab) still
    // narrows the sheet, so it still gets a chip that can clear it.
    for (const id of labelSelected) {
        if (id === UNLABELLED_ID || labels.some(l => l.id === id)) continue;
        chips.push({ key: `label:${id}`, text: 'Removed label', kind: 'label', tone: null, remove: () => removeLabel(id) });
    }
    if (chips.length === 0) return null;

    return (
        <div className="rs-chips" role="group" aria-label="Active filters">
            {chips.map((c) => (
                <button
                    key={c.key}
                    type="button"
                    className="rs-chip qm-inline-action-btn"
                    data-kind={c.kind}
                    aria-label={`Remove filter: ${c.text}`}
                    title={`Remove filter: ${c.text}`}
                    onClick={(e) => {
                        const self = e.currentTarget;
                        const next = (self.nextElementSibling || self.previousElementSibling) as HTMLElement | null;
                        c.remove();
                        try { (next || focusFallbackRef?.current)?.focus(); } catch { /* detached */ }
                    }}
                >
                    {c.kind === 'status'
                        ? <Icons.CheckCircle size={11} aria-hidden="true" />
                        : (
                            <i
                                className="rs-menu__tone"
                                data-empty={c.tone ? undefined : 'true'}
                                style={c.tone ? { ['--label-tone' as any]: c.tone } : undefined}
                                aria-hidden="true"
                            />
                        )}
                    <span className="rs-chip__text">{c.text}</span>
                    <Icons.X className="rs-chip__x" size={11} aria-hidden="true" />
                </button>
            ))}
        </div>
    );
}
